import React, { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useChapters } from '../../hooks/useChapters';
import AppIcon from '../ui/AppIcon';
import { FaLock, FaDiamond, FaStar } from 'react-icons/fa6';

const ChaptersPage = () => {
  const { user, userProfile } = useAuth();
  const { chapters, loading, loadChapters } = useChapters();


  useEffect(() => {
    loadChapters();
  }, [loadChapters]);

  const renderAccessGate = (requiredTier, content) => {
    let hasAccess = false;
    let tierName = '';
    
    switch (requiredTier) {
      case 'basic':
        hasAccess = !!user;
        tierName = 'Login Required';
        break;
      case 'paid':
        hasAccess = userProfile?.hasActivePaidAccess;
        tierName = 'Premium Access Required';
        break;
      case 'subscription':
        hasAccess = userProfile?.subscriptionStatus === 'premium';
        tierName = 'Subscription Required';
        break;
      default:
        hasAccess = false;
        tierName = 'Access Required';
        break;
    }

    if (hasAccess) {
      return content;
    }

    return (
      <div className="access-gate">
        <div className="access-gate-icon">
          {requiredTier === 'basic' ? <FaLock /> : 
           requiredTier === 'paid' ? <FaDiamond /> : <FaStar />}
        </div>
        <div className="access-gate-title">{tierName}</div>
        <div className="access-gate-text">
          {requiredTier === 'basic' 
            ? 'Please log in to view this content'
            : requiredTier === 'paid'
            ? 'Unlock premium content with a one-time purchase'
            : 'Subscribe to access exercises and get answers'
          }
        </div>
        <button className="btn btn-primary">
          {requiredTier === 'basic' ? 'Login' : 'Upgrade Now'}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          Loading chapters...
        </div>
      </div>
    );
  }

  return (
    <div className="chapters-page">
      <div className="container">
        <div className="chapters-header">
          <AppIcon size={60} />
          <h1>Mindfulness Chapters</h1>
          <p>Explore your journey of self-discovery and inner growth</p>
        </div>

        <div className="chapters-grid">
          {chapters.map((chapter) => (
            <div key={chapter.id} className="chapter-card">
              <div className="chapter-header">
                <h3>{chapter.title}</h3>
                <div className="chapter-meta">
                  <span className="chapter-order">Chapter {chapter.order}</span>
                  <span className={`chapter-type ${chapter.type}`}>
                    {chapter.type}
                  </span>
                </div>
              </div>
              
              <p className="chapter-description">{chapter.description}</p>
              
              {/* Basic Content - Requires login */}
              {renderAccessGate('basic', 
                <div className="chapter-content">
                  <h4>Chapter Content</h4>
                  <p>{chapter.content}</p>
                </div>
              )}

              {/* Paid Content */}
              {chapter.paidContent && renderAccessGate('paid',
                <div className="premium-content">
                  <h4>
                    <FaDiamond style={{ color: '#FFD700', marginRight: '8px' }} />
                    Premium Content
                  </h4>
                  <p>{chapter.paidContent}</p>
                </div>
              )}

              {/* Exercises */}
              {chapter.exercises && chapter.exercises.length > 0 && renderAccessGate('subscription',
                <div className="exercises-content">
                  <h4>
                    <FaStar style={{ color: '#28a745', marginRight: '8px' }} />
                    Interactive Exercises
                  </h4>
                  <div className="exercises-list">
                    {chapter.exercises.map((exercise, index) => (
                      <div key={exercise.id} className="exercise-item">
                        <strong>Exercise {index + 1}:</strong> {exercise.question}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {chapters.length === 0 && !loading && (
          <div className="empty-state">
            <AppIcon size={80} />
            <h2>No Chapters Available</h2>
            <p>Chapters will appear here once they're added by an admin.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChaptersPage;
